import { Link, Navigate, useParams } from 'react-router-dom';
import { SiteHeader, SiteFooter } from '../components/SiteChrome';
import { usePageTitle } from '../lib/seo';

// Wraps every placeholder from MIMIQUE-LEGAL-DRAFT.md in a visible amber pill
// so Leanne can see at a glance what still needs filling in before publishing.
const P = ({ children }) => (
    <mark style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 4, fontWeight: 600, fontFamily: 'monospace', fontSize: '0.9em' }}>
        {children}
    </mark>
);

const PAGES = {
    'mentions-legales': {
        title: 'Mentions légales',
        body: (
            <>
                <h2>Éditeur du site</h2>
                <p>
                    Leanne O'Leary Web App Development<br />
                    Entreprise individuelle (micro-entrepreneur)<br />
                    SIRET : 980 503 601 00018<br />
                    Adresse : 35 Bis Rue des Castors, 77000 Melun, France<br />
                    Email : hello@mimique.app
                </p>
                <p>TVA non applicable — article 293 B du Code général des impôts (franchise en base de TVA).</p>

                <h2>Directrice de la publication</h2>
                <p>Leanne O'Leary</p>

                <h2>Hébergement</h2>
                <p>
                    Frontend : Vercel Inc., 340 S Lemon Ave #4133, Walnut, CA 91789, USA<br />
                    Backend : Railway Corporation, 548 Market St Suite 68956, San Francisco, CA 94104, USA<br />
                    Base de données : MongoDB, Inc.
                </p>
            </>
        ),
    },

    // Use and Sale were two pages until 18 August. Merged into one because the
    // split forced readers to hop between them (the plans section pointed at the
    // other page for its own billing terms) and French practice commonly
    // combines them as CGU/CGV. The old /legal/sale-terms URL redirects here.
    'terms': {
        title: 'Terms of Use and Sale',
        subtitle: 'Conditions Générales d\'Utilisation et de Vente',
        body: (
            <>
                <h2>1. What Mimique is</h2>
                <p>Mimique is a French pronunciation and speaking practice application, published by Leanne O'Leary Web App Development ("we", "us"), available at mimique.app. Mimique helps users practise spoken French through the shadowing technique: users add or generate phrases, listen to native audio, record themselves, and receive feedback on pronunciation.</p>

                <h2>2. Eligibility</h2>
                <p>You must be at least 16 years old to create a Mimique account (in line with GDPR's minimum age for consent to data processing without parental authorisation). By creating an account, you confirm you meet this requirement.</p>

                <h2>3. Your account</h2>
                <p>You're responsible for keeping your login details secure and for all activity under your account. Tell us at hello@mimique.app if you suspect unauthorised access.</p>

                <h2>4. Acceptable use</h2>
                <p>You agree not to: use Mimique for anything unlawful; attempt to disrupt, reverse-engineer, or scrape the service; upload content that infringes someone else's rights or is abusive, hateful, or illegal; share your account with others in a way that circumvents subscription limits.</p>

                <h2>5. Your content</h2>
                <p>Phrases you add or generate, and audio recordings you make while practising, remain yours. You grant us a licence to store and process this content solely to provide the service (e.g. running it through pronunciation-scoring and text-to-speech providers — see the Privacy Policy for which third parties are involved). We do not sell your content, and we do not use your voice recordings to train our own AI models.</p>

                <h2>6. Our content</h2>
                <p>The Mimique name, logo, design, and underlying software are our property or licensed to us. You may not copy or redistribute them without permission.</p>

                <h2>7. Free and paid plans</h2>
                <p>Mimique offers a Free plan and paid Pro/Unlimited plans — see the pricing and billing sections below. Feature limits and usage caps for each plan are described on the pricing page and may be adjusted from time to time; we'll give notice of material changes affecting paying subscribers.</p>

                <h2>8. Availability and changes</h2>
                <p>We aim to keep Mimique available and reliable but don't guarantee uninterrupted access. We may update, change, or discontinue features. As a small independent product still in active development, expect ongoing changes and improvements.</p>

                <h2>9. Liability</h2>
                <p>Mimique is provided "as is." Pronunciation feedback is generated by AI and third-party services and is a learning aid, not a certified or guaranteed assessment of your French ability. To the extent permitted by law, we're not liable for indirect or consequential losses arising from your use of the service.</p>

                <h2>10. Termination</h2>
                <p>You can delete your account at any time. We may suspend or terminate accounts that breach these terms.</p>

                <h2>11. Plans and pricing</h2>
                <ul>
                    <li><strong>Free</strong>: no charge.</li>
                    <li><strong>Pro</strong>: €5.99/month (introductory launch price, may increase as features are added — you'll be notified before any price change affects your subscription).</li>
                    <li><strong>Unlimited</strong>: €8.99/month (introductory launch price, may increase as features are added — same notice applies).</li>
                    <li><strong>Founding member discount</strong>: eligible founding members may apply code FOUNDERS50 for 50% off their chosen plan for 12 months from first payment. After 12 months, billing reverts to the then-current standard price for that plan, which you'll be notified of in advance.</li>
                </ul>
                <p>Prices are in euros and include any applicable VAT — currently none is charged, as we operate under the French micro-entreprise VAT exemption (article 293 B CGI).</p>

                <h2>12. Billing</h2>
                <p>Subscriptions are billed monthly in advance via Stripe, our payment processor. Stripe handles all card details directly — we never see or store your full card number. Subscriptions renew automatically each month until cancelled.</p>

                <h2>13. Cancellation</h2>
                <p>You can cancel anytime before your next billing date to avoid being charged for the following month. Cancelling doesn't refund the current billing period already paid for; you keep access until the end of that period, then drop to the Free plan automatically.</p>

                <h2>14. Right of withdrawal (EU consumers)</h2>
                <p>Under EU consumer law, you normally have 14 days to withdraw from an online purchase without giving a reason. Because Mimique's paid features are digital content/services that begin immediately on payment, by subscribing and using paid features straight away you expressly request immediate access and acknowledge that you lose your right of withdrawal once the service has started, in accordance with Article L221-28 of the French Consumer Code. If you'd prefer to keep your withdrawal right, don't use paid features until the 14-day period has passed, and contact us to cancel within that window for a full refund.</p>

                <h2>15. Refunds outside the above</h2>
                <p>Outside the withdrawal-right window, refunds are at our discretion — contact hello@mimique.app and we'll look at it case by case.</p>

                <h2>16. Changes to these terms</h2>
                <p>We may update pricing or these terms. Material changes affecting an existing subscription will be communicated by email in advance.</p>

                <h2>17. Governing law</h2>
                <p>These terms are governed by French law. Disputes will be handled by the competent French courts, without prejudice to any mandatory consumer-protection rights you have in your country of residence if you're an EU consumer.</p>

                <h2>18. Contact</h2>
                <p>hello@mimique.app</p>
            </>
        ),
    },

    'privacy': {
        title: 'Privacy Policy',
        subtitle: 'Politique de confidentialité',
        body: (
            <>
                <h2>Who we are</h2>
                <p>Leanne O'Leary Web App Development (micro-entreprise, SIRET 980 503 601 00018) is the data controller for personal data processed via mimique.app. Contact: hello@mimique.app.</p>

                <h2>What we collect</h2>
                <ul>
                    <li>Account data: email address, password (hashed, never stored in plain text)</li>
                    <li>Content you create: phrases and collections you add or generate</li>
                    <li>Voice recordings: audio you record while practising, processed to give pronunciation feedback</li>
                    <li>Usage data: practice session activity, feature usage, subscription plan and billing status</li>
                    <li>Technical/analytics data: via PostHog — page views, session replay, feature interactions. Session replay masks sensitive fields where applicable.</li>
                    <li>Payment data: handled entirely by Stripe. We receive confirmation of payment/subscription status, never your card details.</li>
                </ul>

                <h2>Why we process it (legal basis)</h2>
                <ul>
                    <li>Providing the service you've signed up for (contract)</li>
                    <li>Billing and subscription management (contract)</li>
                    <li>Improving the product via aggregated usage analytics (legitimate interest)</li>
                    <li>Sending service-related and, where you've opted in, marketing emails (consent / legitimate interest, via MailerLite)</li>
                </ul>

                <h2>Who we share it with</h2>
                <p>We use the following processors to run Mimique, each bound by their own data protection terms:</p>
                <ul>
                    <li>MongoDB Atlas (database hosting)</li>
                    <li>Railway (backend hosting)</li>
                    <li>Vercel (frontend hosting)</li>
                    <li>An AI language processing provider (transcribes and analyses your recordings, generates written feedback, and powers optional AI phrase generation)</li>
                    <li>ElevenLabs (text-to-speech)</li>
                    <li>An AI pronunciation-scoring provider (acoustic analysis of your recordings)</li>
                    <li>Stripe (payment processing)</li>
                    <li>Resend (transactional email)</li>
                    <li>MailerLite (marketing email list)</li>
                    <li>PostHog (product analytics, EU-hosted)</li>
                </ul>
                <p>We do not sell your personal data, and we do not use your voice recordings or practice content to train general-purpose AI models. Some of the above processors are based outside the EU/EEA (e.g. US); where that's the case, transfers rely on their Standard Contractual Clauses or equivalent safeguards.</p>

                <h2>How long we keep it</h2>
                <p>We keep your account and content data for as long as your account is active, and delete it within 30 days of you deleting your account, except where we need to keep billing records longer for legal/tax reasons (French law requires certain accounting records to be kept for several years).</p>

                <h2>Your rights</h2>
                <p>Under GDPR, you can ask us to: access the data we hold on you, correct it, delete it, export it (data portability), or object to/restrict certain processing. Email hello@mimique.app to exercise any of these. You can also lodge a complaint with the CNIL (the French data protection authority) if you're unhappy with how we've handled your data.</p>

                <h2>Cookies</h2>
                <p>Mimique uses essential cookies to keep you signed in. These are required for the service to work and are exempt from consent. We also use analytics cookies (PostHog, EU-hosted) to understand how the app is used. Analytics are optional: we ask on your first visit and nothing is collected unless you accept. You can change your mind at any time by clearing this site's data in your browser, which will bring the choice back.</p>

                <h2>Changes to this policy</h2>
                <p>We'll update this page if how we process data changes materially, and note the date at the top.</p>
            </>
        ),
    },
};

// Slugs that used to be their own page. Kept so links already out in the world
// (the founding-member emails, anything indexed) land on the merged page rather
// than a 404.
const MOVED = { 'sale-terms': 'terms' };

const Legal = () => {
    const { slug } = useParams();
    const page = PAGES[slug];
    usePageTitle(page?.title);

    if (!page && MOVED[slug]) {
        return <Navigate to={`/legal/${MOVED[slug]}`} replace />;
    }

    if (!page) {
        return (
            <div style={{ minHeight: '100vh', padding: '80px 24px', background: '#ffffff' }}>
                <div style={{ maxWidth: 720, margin: '0 auto' }}>
                    <h1>Page not found</h1>
                    <p><Link to="/">Back to Mimique</Link></p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: '#ffffff', color: '#1a5235', fontFamily: "'Inter', sans-serif" }}>
            <SiteHeader />

            {/* The outer wrapper matches the header's 1160/24px container, so the
                text starts on the same vertical line as the logo above it. The
                inner article keeps a readable measure. */}
            <div style={{ maxWidth: 1160, margin: '0 auto', padding: '64px 24px 100px' }}>
                <article style={{ maxWidth: 760 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#2d7a4f', marginBottom: 12 }}>Legal</div>
                    <h1 style={{ margin: '0 0 8px', fontFamily: "'Nunito Sans', sans-serif", fontSize: 38, fontWeight: 700, letterSpacing: '-0.02em', color: '#1a5235' }}>
                        {page.title}
                    </h1>
                    {page.subtitle && (
                        <div style={{ margin: '0 0 40px', fontSize: 15, fontStyle: 'italic', color: '#5b7c68' }}>{page.subtitle}</div>
                    )}
                    {!page.subtitle && <div style={{ marginBottom: 40 }} />}

                    <div className="legal-body" style={{ fontSize: 15.5, lineHeight: 1.75, color: '#4a6357' }}>
                        {page.body}
                    </div>
                </article>
            </div>

            <SiteFooter />

            <style>{`
                .legal-body h2 {
                    font-family: 'Nunito Sans', sans-serif;
                    font-size: 20px;
                    font-weight: 800;
                    color: #1a5235;
                    margin-top: 32px;
                    margin-bottom: 12px;
                    letter-spacing: -0.01em;
                }
                .legal-body p { margin: 0 0 14px; }
                .legal-body ul { margin: 0 0 14px; padding-left: 22px; }
                .legal-body li { margin-bottom: 6px; }
                .legal-body a { color: #2d7a4f; }
            `}</style>
        </div>
    );
};

export default Legal;
